import React, {useCallback, useEffect, useReducer, useRef} from 'react';
import moment from 'moment';

import {Icon} from '@albert-io/atomic';
import {CleverMark} from 'client/Clever/CleverMark';
import {
  CleverNotLoggedInModal,
  CleverSyncModal,
  createSyncJob,
  pollCleverCourseSync
} from 'client/Clever/CleverSync';
import {
  CleverSyncContext,
  CleverSyncDispatch,
  CleverSyncReducer,
  initialCleverSyncState
} from 'client/Clever/CleverSyncProvider';
import type {CleverSyncResults, CleverUser} from 'client/Clever/util';
import {getUserQuery} from 'client/Clever/util';
import {invalidateClassroomsQueries} from 'client/Classrooms/Teacher/NewClassroomModal/newClassroomModal.utils';

import {useTeacherClassroomActions} from '../hooks/useTeacherClassrooms';

import {ActionCard} from './ActionCard';

export const CleverImportCard = ({user}: {user: CleverUser}) => {
  const [state, dispatch] = useReducer(CleverSyncReducer, user, initialCleverSyncState);
  const {fetchClassrooms, fetchArchivedClassrooms} = useTeacherClassroomActions();

  const onStart = useCallback(() => {
    dispatch({type: 'setLoading', value: true});
  }, []);

  const onComplete = useCallback((syncResults: CleverSyncResults) => {
    dispatch({type: 'setLoading', value: false});
    dispatch({type: 'setSyncResults', syncResults});
    // Clear the in-progress id (set when resuming an auto-triggered sync) so the card
    // leaves its "Syncing…" state once polling resolves.
    dispatch({type: 'setCurrentlyInProgressSyncId'});
    if (syncResults.success && syncResults.results.some((r) => r.success)) {
      dispatch({type: 'setSyncDate', date: moment()});
    }
    // Surface the results modal on anything the teacher should review: a full failure,
    // any per-class failure, or a class auto-archived because it was removed from Clever.
    const needsReview =
      !syncResults.success ||
      !syncResults.results.every((r) => r.success) ||
      syncResults.results.some((r) => r.success && r.status === 'archived');
    if (needsReview) {
      dispatch({type: 'openModal'});
    }
  }, []);

  // Resume polling a sync that is already running — auto-triggered on login (backend plug) or
  // still in progress from a prior visit. The returned cleanup stops the poll on unmount and
  // covers StrictMode's mount→cleanup→remount (clears the first poll, the remount starts one).
  // onComplete clears currentlyInProgressSyncId when the poll resolves, so the resulting re-run
  // early-returns instead of starting a second poll.
  useEffect(() => {
    if (!state.currentlyInProgressSyncId) {
      return undefined;
    }
    onStart();
    return pollCleverCourseSync(state.currentlyInProgressSyncId, onComplete);
  }, [state.currentlyInProgressSyncId, onStart, onComplete]);

  // When any sync finishes (resume, inline re-sync, or the first-import modal — all of
  // which land here via syncResults), refresh dashboard data. invalidateClassroomsQueries
  // only covers the legacy classes page, so the v2 sidemenu/store needs an explicit
  // refetch or Clever-created and auto-archived classes won't appear until reload.
  const prevResultsRef = useRef(state.syncResults);
  useEffect(() => {
    const justCompleted = state.syncResults && state.syncResults !== prevResultsRef.current;
    prevResultsRef.current = state.syncResults;
    if (justCompleted) {
      getUserQuery().invalidateInterest();
      invalidateClassroomsQueries();
      fetchClassrooms();
      fetchArchivedClassrooms();
    }
  }, [state.syncResults, fetchClassrooms, fetchArchivedClassrooms]);

  const isSyncing = state.loading || Boolean(state.currentlyInProgressSyncId);

  // A re-sync click starts its poll inside createSyncJob's async save, so the poll cleanup comes
  // back later. Hold it to clearInterval on unmount; if the card already unmounted by the time it
  // resolves, stop the poll immediately so it never outlives the card.
  const mountedRef = useRef(true);
  const resyncStopRef = useRef<(() => void) | undefined>(undefined);
  useEffect(() => {
    // Set on (re)mount, not just at declaration, so a StrictMode mount→cleanup→remount leaves
    // mountedRef true — otherwise a later re-sync would stop its own poll immediately.
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      resyncStopRef.current?.();
    };
  }, []);

  const handleClick = () => {
    if (!state.userCleverId || !state.userCleverDistrictId) {
      // Allowed for Clever but not logged in via Clever — prompt to re-login.
      dispatch({type: 'openLogoutModal'});
    } else if (state.lastSyncDate) {
      // Re-sync runs immediately, no confirm step (matches the legacy inline button).
      createSyncJob(onStart, onComplete).then((stopPolling) => {
        if (mountedRef.current) {
          resyncStopRef.current = stopPolling;
        } else {
          stopPolling?.();
        }
      });
    } else {
      // First import opens the confirm modal explaining the bulk import.
      dispatch({type: 'openModal'});
    }
  };

  let iconContent: React.ReactNode;
  let title: string;
  let description: string | undefined;
  if (isSyncing) {
    iconContent = <Icon icon='arrows-rotate' iconStyle='light' size='inherit' spin />;
    title = 'Syncing...';
    description = undefined;
  } else if (state.lastSyncDate) {
    iconContent = <Icon icon='arrows-rotate' iconStyle='light' size='inherit' />;
    title = 'Re-sync with Clever';
    description = `Last sync: ${state.lastSyncDate.format('MMM D, YYYY')}`;
  } else {
    iconContent = <CleverMark />;
    title = 'Import & Sync with Clever';
    description = 'Sync your roster and classes with Clever.';
  }

  return (
    <CleverSyncContext.Provider value={state}>
      <CleverSyncDispatch.Provider value={dispatch}>
        <ActionCard
          cardClassName='elevation-shadow-l3'
          data-testid='home-action__import-clever'
          description={description}
          busy={isSyncing}
          iconClassName='background-blueberry-subtle content-blueberry'
          iconContent={iconContent}
          onClick={handleClick}
          title={title}
        />
        {state.isModalOpen && <CleverSyncModal />}
        {state.isLogoutModalOpen && <CleverNotLoggedInModal />}
      </CleverSyncDispatch.Provider>
    </CleverSyncContext.Provider>
  );
};
